import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CirclularStatusIcon from "./CirclularStatusIcon";
import { ReactComponent as XIcon } from "../../assets/icons/x.svg";
import React, { useEffect, useMemo, useRef, useState } from "react";
import iconUtil from "../../utils/iconUtil";
import { useTimeAgo } from "../../hooks/useTimeAgo";
import { setPlanViewStateLoading } from "../../store/realTimeUpdates";
import { createPlanViewStateKey } from "../../store/realTimeUpdates/stateKeyUtils";
import { useQueryParams } from "../../hooks/useQueryParams";
import {
  initiateOptimization,
  stopOptimization,
} from "../../store/ganttChart/action";
import IconButtonComponent from "../UI/buttons/IconButton";
import {
  determinePondooStatusBarState,
  getProgressBarValue,
  getTabLabel,
} from "../../utils/statusBarUtils";
import ProgressBar from "./ProgressBar";
import { useSolutionPlanView } from "../../hooks/useSolutionPlanView";
import {
  setIsPondooPlanReady,
  setOptimizedStatusLabel,
} from "../../store/pondooStatusBar";
import labels from "../../utils/labels";
import RulesetSelector from "../ganttChart/RulesetSelector";
import { isRulesetSelectorShown } from "../../utils/environmentUtils";
import useDataSelector from "../../hooks/useDataSelector";
import StandardButton from "../UI/buttons/StandardButton";
import { ReactComponent as StarsIcon } from "../../assets/icons/stars_white.svg";
const PondooPlanStatusBar = () => {
  const dispatch = useAppDispatch();
  const { dataToUse, idScenario: ganttIdScenario } = useDataSelector();
  const { isPondooPlanReady } = useAppSelector(
    (state) => state.pondooStatusBarSlice,
  );
  const { selectedGanttChartTab } = useAppSelector(
    (state) => state.ganttChartSlice,
  );

  /**
   * Dynamic display state given the user's selection of filter and date
   */
  const planViewState = useSolutionPlanView("Optimised");
  const queryKey = useQueryParams("Optimised");

  // A newer run can replace the scenario while the previous chart is still cached.
  const currentRunScenario =
    planViewState.job?.jobRef === planViewState.activeJobRef
      ? planViewState.job?.idScenario
      : undefined;
  const idScenarioToUse =
    currentRunScenario ?? ganttIdScenario ?? planViewState.idScenario;

  // const planViewState: PlanViewStateEntry = { status: "ready" };

  const startedTimeAgo = useTimeAgo(planViewState?.job?.createdDateTime);
  const completedTimeAgo = useTimeAgo(planViewState?.job?.completedDateTime);
  const isOutdated = !!planViewState?.job?.outdatedDateTime;

  useEffect(() => {
    if (planViewState.status === "ready" && !isPondooPlanReady)
      dispatch(setIsPondooPlanReady(true));
    else if (isPondooPlanReady && planViewState.status !== "ready") {
      dispatch(setIsPondooPlanReady(false));
    }
  }, [planViewState.status]);

  const pondooStatusBarObject = useMemo(() => {
    return determinePondooStatusBarState(
      planViewState,
      completedTimeAgo,
      isOutdated,
    );
  }, [planViewState, completedTimeAgo, isOutdated]);

  useEffect(() => {
    dispatch(
      setOptimizedStatusLabel(
        getTabLabel(planViewState, completedTimeAgo),
      ),
    );
  }, [planViewState, completedTimeAgo, startedTimeAgo]);

  const starIconRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (starIconRef.current) {
      iconUtil.updateIconColor(
        starIconRef.current,
        "var(--col-interaction-tertiary)",
      );
    }
  }, [pondooStatusBarObject]);

  // Progress bar update based on job start time
  const [progressBarValue, setProgressBarValue] = useState<number>(0);
  useEffect(() => {
    // No job yet (the run still fetches from Nexus): the bar waits at 0
    if (planViewState.status === "loading") {
      setProgressBarValue(getProgressBarValue(startedTimeAgo) ?? 0);
    }
  }, [planViewState, startedTimeAgo]);

  // Functions
  const updatePlan = (idScenario: number) => {
    if (queryKey.filterId && queryKey.date) {
      dispatch(
        setPlanViewStateLoading(
          createPlanViewStateKey(
            "PondooSolution",
            queryKey.filterId,
            queryKey.date,
          ),
        ),
      );
    }
    dispatch(initiateOptimization(idScenario));
  };

  const buttonAction = () => {
    if (idScenarioToUse) {
      if (
        planViewState.status === "ready" ||
        planViewState.status === "error" ||
        planViewState.status === "missing"
      ) {
        updatePlan(idScenarioToUse);
      }
    }
  };

  const abortOptimization = () => {
    if (idScenarioToUse) {
      if (planViewState.status === "loading") {
        dispatch(stopOptimization(idScenarioToUse));
      }
    }
  };

  return (
    <div
      style={{ height: "40px" }}
      className="flex gap-[--spacing-sm] items-center"
    >
      {pondooStatusBarObject && (
        <>
          <div className="flex items-center gap-[10px] justofy-start">
            <span className="flex items-center justify-center w-[30px]">
              <CirclularStatusIcon status={pondooStatusBarObject?.status} />
            </span>
            <p className="visible text-nowrap">{pondooStatusBarObject.title}</p>
          </div>
          {isRulesetSelectorShown &&
            dataToUse &&
            selectedGanttChartTab === "Original" && <RulesetSelector />}

          {pondooStatusBarObject.buttonTitle && (
            <StandardButton
              ariaLabel={pondooStatusBarObject.buttonTitle}
              onClick={() => {
                buttonAction();
              }}
              variant="SECONDARY"
              iconRight={
                <span ref={starIconRef}>
                  <StarsIcon />
                </span>
              }
            >
              <span>{pondooStatusBarObject.buttonTitle}</span>
            </StandardButton>
          )}
          {(pondooStatusBarObject.status === "Running" ||
            pondooStatusBarObject.status === "NotScheduled" ||
            pondooStatusBarObject.status === "Scheduled") && (
            <div className="flex items-center">
              <ProgressBar progressBarValue={progressBarValue}></ProgressBar>
              <IconButtonComponent
                ariaLabel={labels.abortOptimization}
                id="x"
                icon={<XIcon />}
                onClick={abortOptimization}
              ></IconButtonComponent>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(PondooPlanStatusBar);
